/**
 * Codisty Theme Engine · runtime (vanilla, no dependencies)
 * Custom elements: <cs-slider>, <cs-tabs>, <cs-finder>, <cs-quick-add>, <cs-bundle>, <cs-follow>
 * Integrates with Horizon's cart drawer via @shopify/events (CartLinesUpdateEvent).
 */
document.documentElement.classList.add('cs-js');

const designMode = Boolean(window.Shopify && window.Shopify.designMode);
const routes = (() => {
  try {
    // eslint-disable-next-line no-undef
    if (typeof Theme !== 'undefined' && Theme.routes) return Theme.routes;
  } catch (e) { /* ignore */ }
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return { cart_add_url: `${root}cart/add.js`, cart_url: `${root}cart` };
})();
const t = (key, fallback) => (window.CodistyStrings && window.CodistyStrings[key]) || fallback;

/* ------------------------------------------------------------------ */
/* Cart: add lines and notify Horizon (cart icon + drawer)              */
/* ------------------------------------------------------------------ */
export async function codistyAddToCart(lines, sourceEl, source = 'codisty') {
  let CartLinesUpdateEvent = null;
  try { ({ CartLinesUpdateEvent } = await import('@shopify/events')); } catch (e) { /* not a Horizon theme */ }

  const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
  const items = lines.map((l) => ({ id: Number(l.id), quantity: Number(l.quantity) || 1, ...(l.properties ? { properties: l.properties } : {}) }));
  const total = items.reduce((n, i) => n + i.quantity, 0);
  const deferred = CartLinesUpdateEvent ? CartLinesUpdateEvent.createPromise() : null;

  if (CartLinesUpdateEvent) {
    (sourceEl || document).dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'product',
      lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
      promise: deferred.promise,
    }));
  }

  try {
    const res = await fetch(routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sectionIds.join(',') }),
    });
    const json = await res.json();
    const cart = await (await fetch(`${routes.cart_url}.json`, { headers: { Accept: 'application/json' } })).json();
    const failed = Boolean(json.status);
    if (deferred) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source, sourceId: source, itemCount: failed ? 0 : total, sections: json.sections, didError: failed },
      });
    } else {
      document.dispatchEvent(new CustomEvent('codisty:cart-updated', { detail: { cart } }));
    }
    if (failed) throw new Error(json.description || json.message || 'Could not add to cart');
    return cart;
  } catch (err) {
    if (deferred) deferred.reject(err);
    throw err;
  }
}

/* ------------------------------------------------------------------ */
/* <cs-slider>: scroll-snap slider with arrows, dots, autoplay, loop    */
/* ------------------------------------------------------------------ */
class CsSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.cs-slider__track');
    if (!this.track) return;
    this.slides = [...this.track.children];
    this.prev = this.querySelector('[data-cs-prev]');
    this.next = this.querySelector('[data-cs-next]');
    this.dotsWrap = this.querySelector('.cs-slider__dots');
    this.loop = this.dataset.loop === 'true';
    this.autoplayMs = Number(this.dataset.autoplay || 0) * 1000;

    this.prev?.addEventListener('click', () => this.go(this.index() - 1));
    this.next?.addEventListener('click', () => this.go(this.index() + 1));
    this.track.addEventListener('scroll', () => { cancelAnimationFrame(this._raf); this._raf = requestAnimationFrame(() => this.update()); }, { passive: true });
    this.buildDots();
    this.update();

    if (this.autoplayMs && !designMode && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.start();
      ['mouseenter', 'focusin', 'touchstart'].forEach((e) => this.addEventListener(e, () => this.stop(), { passive: true }));
      ['mouseleave', 'focusout'].forEach((e) => this.addEventListener(e, () => this.start()));
    }
    // Theme editor: show the selected slide
    this.addEventListener('shopify:block:select', (e) => {
      const i = this.slides.findIndex((s) => s.contains(e.target));
      if (i >= 0) this.go(i, false);
    });
  }
  disconnectedCallback() { this.stop(); }
  pageSize() {
    const first = this.slides[0];
    if (!first) return 1;
    return Math.max(1, Math.round(this.track.clientWidth / first.getBoundingClientRect().width));
  }
  pages() { return Math.max(1, this.slides.length - this.pageSize() + 1); }
  index() {
    const left = Math.abs(this.track.scrollLeft);
    const w = this.slides[0]?.getBoundingClientRect().width || 1;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return Math.round(left / (w + gap));
  }
  go(i, smooth = true) {
    const pages = this.pages();
    if (this.loop) i = (i + pages) % pages; else i = Math.max(0, Math.min(i, pages - 1));
    const target = this.slides[i];
    if (!target) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    const offset = rtl ? -(this.track.getBoundingClientRect().right - target.getBoundingClientRect().right) : target.offsetLeft - this.track.offsetLeft;
    this.track.scrollTo({ left: rtl ? this.track.scrollLeft + offset : offset, behavior: smooth ? 'smooth' : 'auto' });
  }
  buildDots() {
    if (!this.dotsWrap) return;
    this.dotsWrap.innerHTML = '';
    const pages = this.pages();
    if (pages < 2) { this.querySelector('.cs-slider__nav')?.setAttribute('hidden', ''); return; }
    for (let i = 0; i < pages; i++) {
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'cs-slider__dot';
      b.setAttribute('aria-label', `${t('slide', 'Slide')} ${i + 1}`);
      b.addEventListener('click', () => this.go(i));
      this.dotsWrap.appendChild(b);
    }
  }
  update() {
    const i = this.index();
    this.dotsWrap?.querySelectorAll('.cs-slider__dot').forEach((d, n) => d.setAttribute('aria-current', String(n === i)));
    if (!this.loop) {
      if (this.prev) this.prev.disabled = i <= 0;
      if (this.next) this.next.disabled = i >= this.pages() - 1;
    }
  }
  start() { this.stop(); this._timer = setInterval(() => this.go(this.index() + 1), this.autoplayMs); }
  stop() { clearInterval(this._timer); }
}

/* ------------------------------------------------------------------ */
/* <cs-tabs>                                                           */
/* ------------------------------------------------------------------ */
class CsTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (e) => {
        const dir = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
        if (e.key === 'ArrowRight') this.select((i + dir + this.tabs.length) % this.tabs.length, true);
        if (e.key === 'ArrowLeft') this.select((i - dir + this.tabs.length) % this.tabs.length, true);
      });
    });
    this.addEventListener('shopify:block:select', (e) => {
      const i = this.tabs.findIndex((tab) => tab.id === e.target.dataset?.tabFor || tab.contains(e.target));
      if (i >= 0) this.select(i);
    });
  }
  select(i, focus = false) {
    this.tabs.forEach((tab, n) => {
      const on = n === i;
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
      const panel = this.querySelector(`#${tab.getAttribute('aria-controls')}`);
      if (panel) panel.hidden = !on;
    });
    if (focus) this.tabs[i].focus();
  }
}

/* ------------------------------------------------------------------ */
/* <cs-finder>: builds filtered collection URL + live result count     */
/* ------------------------------------------------------------------ */
class CsFinder extends HTMLElement {
  connectedCallback() {
    this.base = this.dataset.url;
    this.countEl = this.querySelector('[data-cs-count]');
    this.link = this.querySelector('[data-cs-results]');
    // Dropdowns mirror the radio buttons of their question (radios stay the source of truth)
    this.querySelectorAll('[data-cs-finder-select]').forEach((select) => {
      const radios = [...select.closest('fieldset').querySelectorAll('input[type="radio"]')];
      select.addEventListener('change', () => { const r = radios[select.selectedIndex]; if (r) r.checked = true; });
      radios.forEach((r, i) => r.addEventListener('change', () => { select.selectedIndex = i; }));
      const on = radios.findIndex((r) => r.checked); if (on >= 0) select.selectedIndex = on;
    });
    this.addEventListener('change', () => this.refresh());
    this.refresh();
  }
  params() {
    const p = new URLSearchParams();
    this.querySelectorAll('input:checked').forEach((input) => {
      if (!input.value) return;
      const name = input.dataset.param;
      // Support "price" ranges like "filter.v.price.gte=10&filter.v.price.lte=20"
      if (input.value.includes('=')) new URLSearchParams(input.value).forEach((v, k) => p.append(k, v));
      else if (name) p.append(name, input.value);
    });
    return p;
  }
  async refresh() {
    const p = this.params();
    const url = `${this.base}${p.toString() ? `?${p}` : ''}`;
    if (this.link) this.link.href = url;
    if (!this.countEl) return;
    clearTimeout(this._t);
    this._t = setTimeout(async () => {
      try {
        const q = new URLSearchParams(p); q.set('section_id', 'cs-finder-count');
        const html = await (await fetch(`${this.base}?${q}`)).text();
        const n = parseInt(new DOMParser().parseFromString(html, 'text/html').querySelector('[data-count]')?.dataset.count ?? '', 10);
        if (!Number.isNaN(n)) this.countEl.textContent = n;
      } catch (e) { /* keep last count */ }
    }, 200);
  }
}

/* ------------------------------------------------------------------ */
/* <cs-quick-add>                                                      */
/* ------------------------------------------------------------------ */
class CsQuickAdd extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const id = new FormData(this.form).get('id');
      if (!id) return;
      this.form.classList.add('is-loading');
      try {
        const picked = this.form.querySelector('select[name="id"]')?.selectedOptions[0] || this.form.querySelector('input[name="id"]');
        const line = { id, quantity: 1 };
        if (picked?.hasAttribute('data-preorder')) line.properties = { _preorder: '1' }; // checkout requires prepayment
        await codistyAddToCart([line], this, 'codisty-quick-add');
        this.form.classList.add('is-added');
        setTimeout(() => this.form.classList.remove('is-added'), 2000);
      } catch (err) {
        alert(err.message);
      } finally {
        this.form.classList.remove('is-loading');
      }
    });
  }
}

/* ------------------------------------------------------------------ */
/* <cs-bundle>: add every bundle item, optionally apply a discount code */
/* ------------------------------------------------------------------ */
class CsBundle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-cs-bundle-add]');
    this.button?.addEventListener('click', async () => {
      const lines = [...this.querySelectorAll('[data-variant-id]')].map((el) => ({ id: el.dataset.variantId, quantity: 1 })).filter((l) => l.id);
      if (!lines.length) return;
      const label = this.button.textContent;
      this.button.disabled = true; this.button.textContent = t('adding', 'Adding…');
      try {
        const code = this.dataset.discount;
        if (code) await fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' });
        await codistyAddToCart(lines, this, 'codisty-bundle');
        this.button.textContent = t('added', 'Added to cart');
      } catch (err) {
        alert(err.message); this.button.textContent = label;
      } finally {
        setTimeout(() => { this.button.disabled = false; this.button.textContent = label; }, 2200);
      }
    });
  }
}

/* ------------------------------------------------------------------ */
/* <cs-follow>: brand follow via the Codisty app proxy                  */
/* Logged in → one click. Guest → email form (remembered for next time). */
/* Hidden until /apps/codisty/follow answers, so it never shows broken.  */
/* ------------------------------------------------------------------ */
const CS_FOLLOW_URL = '/apps/codisty/follow';
let csFollowReady;
const csStore = { get: (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } }, set: (k, v) => { try { localStorage.setItem(k, v); } catch (e) { /* ignore */ } } };
class CsFollow extends HTMLElement {
  connectedCallback() {
    this.btn = this.querySelector('.cs-follow');
    this.form = this.querySelector('form');
    this.msg = this.querySelector('.cs-follow__msg');
    this.brand = this.dataset.brand;
    this.customer = !!this.dataset.customer;
    if (!this.btn || !this.brand) return;
    if (!this.customer && csStore.get(`cs-follow:${this.brand}`) === '1') this.render(true);
    csFollowReady ||= fetch(CS_FOLLOW_URL, { headers: { Accept: 'application/json' } }).then((r) => r.ok ? r.json() : null).then((j) => !!(j && j.ok)).catch(() => false);
    csFollowReady.then((ok) => { if (ok) this.hidden = false; });
    this.btn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this.toggle(); });
    this.form?.addEventListener('submit', (e) => { e.preventDefault(); this.submitEmail(); });
  }
  get on() { return this.btn.getAttribute('aria-pressed') === 'true'; }
  render(on) {
    this.btn.setAttribute('aria-pressed', String(on));
    this.btn.textContent = on ? this.dataset.following : this.dataset.follow;
  }
  say(text) { if (!this.msg) return; this.msg.textContent = text || ''; this.msg.hidden = !text; }
  async send(follow, email) {
    this.btn.disabled = true;
    try {
      const r = await fetch(CS_FOLLOW_URL, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ brand: this.brand, follow, email }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || r.status);
      this.render(follow);
      if (!this.customer) { csStore.set(`cs-follow:${this.brand}`, follow ? '1' : '0'); if (email) csStore.set('cs-follow-email', email); }
      this.say(follow ? this.dataset.done : '');
      return true;
    } catch (e) {
      this.say(this.dataset.error);
      return false;
    } finally { this.btn.disabled = false; }
  }
  toggle() {
    const email = csStore.get('cs-follow-email');
    if (this.customer) return this.send(!this.on);
    if (this.on) { this.render(false); csStore.set(`cs-follow:${this.brand}`, '0'); this.say(''); return email ? this.send(false, email) : null; }
    if (email) return this.send(true, email);
    if (!this.form) return null;
    this.form.hidden = !this.form.hidden;
    this.btn.setAttribute('aria-expanded', String(!this.form.hidden));
    if (!this.form.hidden) this.form.querySelector('input')?.focus();
  }
  async submitEmail() {
    const input = this.form.querySelector('input[type=email]');
    const email = input.value.trim();
    if (!input.checkValidity() || !email) { input.reportValidity(); return; }
    const submit = this.form.querySelector('[type=submit]'); submit.disabled = true;
    const ok = await this.send(true, email);
    submit.disabled = false;
    if (ok) { this.form.hidden = true; this.btn.setAttribute('aria-expanded', 'false'); }
  }
}

/* ------------------------------------------------------------------ */
/* Mobile tab bar: keep the cart count in sync                          */
/* ------------------------------------------------------------------ */
async function csRefreshCartCount() {
  const els = document.querySelectorAll('[data-cs-cart-count]');
  if (!els.length) return;
  try {
    const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } })).json();
    els.forEach((el) => { el.textContent = cart.item_count > 99 ? '99+' : cart.item_count; el.hidden = !cart.item_count; });
  } catch (e) { /* ignore */ }
}
if (document.querySelector('[data-cs-cart-count]') || document.readyState === 'loading') {
  import('@shopify/events').then(({ StandardEvents }) => {
    document.addEventListener(StandardEvents.cartLinesUpdate, (e) => { (e.promise || Promise.resolve()).catch(() => {}).finally(() => setTimeout(csRefreshCartCount, 150)); });
  }).catch(() => {});
  document.addEventListener('codisty:cart-updated', csRefreshCartCount);
  window.addEventListener('pageshow', (e) => { if (e.persisted) csRefreshCartCount(); });
}

/* Tab bar search button: full-screen search dialog with instant results */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-cs-tabbar-search]');
  if (btn) {
    const dialog = document.getElementById(btn.dataset.csTabbarSearch);
    if (!dialog || typeof dialog.showModal !== 'function') return; // fall back to the search page link
    e.preventDefault();
    dialog.showModal();
    document.documentElement.classList.add('cs-search-open');
    dialog.querySelector('.cs-search__input')?.focus(); // same tap → the keyboard opens on phones
    return;
  }
  const dialog = e.target.closest('.cs-search-modal');
  if (dialog && (e.target.closest('[data-cs-search-close]') || e.target === dialog)) dialog.close();
});
document.addEventListener('close', (e) => { if (e.target.classList?.contains('cs-search-modal')) document.documentElement.classList.remove('cs-search-open'); }, true);

/* ------------------------------------------------------------------ */
/* <cs-budget-bundle>: budget → machine + grinder + beans ("Picked by    */
/* CodiGuide"). Tries every in-stock combination within the budget and    */
/* keeps the best balanced one; each slot can be swapped. Items are added */
/* with the hidden property _cs_bundle so the app's discount applies.     */
/* ------------------------------------------------------------------ */
class CsBudgetBundle extends HTMLElement {
  connectedCallback() {
    try { this.data = JSON.parse(this.querySelector('[data-catalog]').textContent); } catch (e) { return; }
    this.items = this.querySelector('[data-items]');
    this.empty = this.querySelector('[data-empty]');
    this.pct = Number(this.dataset.discount) || 0;
    this.fmt = this.dataset.moneyFormat || '${{amount}}';
    this.addEventListener('change', () => this.pick());
    this.pick();
  }
  money(cents) {
    const n = cents / 100;
    const f = (x, sep = ',', dec = '.') => x.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, sep).replace(/\.(\d+)$/, `${dec}$1`);
    return this.fmt.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, f(n, '.', ',')).replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(n))).replace(/\{\{\s*amount\s*\}\}/, f(n));
  }
  get budget() { return Number(this.querySelector('input[name^="budget-"]:checked')?.value || 0) * 100; }
  /** Lower end of the chosen range = the next smaller budget option (so "$300 – $600" prefers sets above $300). */
  get floor() {
    const max = this.budget;
    const values = [...this.querySelectorAll('input[name^="budget-"]')].map((i) => Number(i.value) * 100).filter((v) => v < max);
    return values.length ? Math.max(...values) : 0;
  }
  get hasGrinder() { return this.querySelector('input[name^="grinder-"]:checked')?.value === 'have'; }
  /**
   * All sets within budget, best first: sets inside the chosen range first, then by score. The score favours the
   * machine, then the grinder; beans: a popular bag around $20. A customer who owns a grinder is steered away
   * from machines with a built-in one; otherwise built-in machines or machine + grinder are both fine.
   */
  sets() {
    const { machines = [], grinders = [], beans = [] } = this.data;
    const budget = this.budget; if (!budget) return [];
    const floor = this.floor; const owns = this.hasGrinder;
    const beanList = beans.map((b, rank) => ({ ...b, rank })).filter((b) => b.price <= 4000 && !/decaf|\bdek\b|swiss water|منزوع/i.test(b.title)); // best-selling first; no decaf as a default pick
    const out = [];
    for (const m of machines) {
      if (m.price > budget) continue;
      const needGrinder = !m.builtIn && !owns;
      const gList = needGrinder ? grinders.filter((g) => g.price <= Math.max(m.price * 2.5, 150)) : [null];
      for (const g of gList) {
        const rest = budget - m.price - (g ? g.price : 0);
        if (rest < 0) continue;
        for (const b of beanList) {
          if (b.price > rest) continue;
          const total = m.price + (g ? g.price : 0) + b.price;
          const beanFit = -Math.abs(b.price - Math.min(2000, rest)) * 0.2 - b.rank * 40;
          const ownsPenalty = owns && m.builtIn ? m.price * 0.6 : 0; // they already have a grinder: don't pay for another
          out.push({ m, g, b, total, inRange: total >= floor, score: m.price + (g ? g.price * 0.6 : 0) + beanFit - ownsPenalty });
        }
      }
    }
    return out.sort((x, y) => (y.inRange - x.inRange) || (y.score - x.score));
  }
  pick() {
    this.all = this.sets();
    this.current = this.all[0] || null;
    this.seen = new Set(this.current ? [this.id(this.current)] : []);
    this.render();
  }
  id(set) { return `${set.m.id}-${set.g?.id || 0}-${set.b.id}`; }
  /** "Show another set": a different good set in the same range, preferring a new machine or grinder. */
  shuffle() {
    if (!this.current || this.all.length < 2) return;
    const best = this.all[0];
    const good = this.all.filter((set) => set.inRange === best.inRange && set.score >= best.score * 0.45);
    const pool = good.length > 1 ? good : this.all;
    const c = this.current;
    const differs = (set) => set.m.id !== c.m.id || (set.g?.id || 0) !== (c.g?.id || 0);
    let options = pool.filter((set) => differs(set) && !this.seen.has(this.id(set)));
    if (!options.length) options = pool.filter((set) => this.id(set) !== this.id(c) && !this.seen.has(this.id(set)));
    if (!options.length) { this.seen = new Set([this.id(c)]); options = pool.filter((set) => this.id(set) !== this.id(c)); }
    if (!options.length) return;
    const top = options.slice(0, 12);
    this.current = top[Math.floor(Math.random() * top.length)];
    this.seen.add(this.id(this.current));
    this.render();
  }
  /** Sets that keep the other slots: machine → any; grinder → same machine; beans → same machine + grinder. */
  pool(slot) {
    const c = this.current; const gid = (set) => set.g?.id || 0;
    if (slot === 'm') return this.all;
    if (slot === 'g') return this.all.filter((set) => set.m.id === c.m.id);
    return this.all.filter((set) => set.m.id === c.m.id && gid(set) === gid(c));
  }
  key(slot, set) { return slot === 'm' ? set.m.id : slot === 'g' ? set.g?.id || 0 : set.b.id; }
  canSwap(slot) { return !!this.current && new Set(this.pool(slot).map((set) => this.key(slot, set))).size > 1; }
  swap(slot) {
    if (!this.canSwap(slot)) return;
    const best = new Map(); // first (best-scored) set for each option, in score order
    for (const set of this.pool(slot)) { const k = this.key(slot, set); if (!best.has(k)) best.set(k, set); }
    const options = [...best.values()];
    const i = options.findIndex((set) => this.key(slot, set) === this.key(slot, this.current));
    this.current = options[(i + 1) % options.length];
    this.render();
  }
  card(slot, item, label, note) {
    const esc = (v) => String(v ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    if (!item) {
      const icon = this.querySelector(note === this.data.labels.builtin ? '[data-icon-builtin]' : '[data-icon-owned]')?.innerHTML || '';
      return `<div class="cs-bundle__item cs-bbundle__item--note"><span class="cs-bundle__img cs-bbundle__icon">${icon}</span><small>${esc(label)}</small><b>${esc(note)}</b></div>`;
    }
    return `<div class="cs-bundle__item"><a class="cs-bundle__img" href="${esc(item.url)}">${item.img ? `<img src="${esc(item.img)}" alt="" loading="lazy">` : ''}</a><small>${esc(label)}</small><a class="cs-bbundle__name" href="${esc(item.url)}"><b>${esc(item.title)}</b></a><span>${this.money(item.price)}</span>${note ? `<em class="cs-bbundle__note">${esc(note)}</em>` : ''}${this.canSwap(slot) ? `<button type="button" class="cs-bbundle__swap" data-swap="${slot}">↻ ${esc(this.data.labels.swap)}</button>` : ''}</div>`;
  }
  render() {
    const L = this.data.labels; const set = this.current;
    this.empty.hidden = !!set;
    if (!set) { this.items.innerHTML = ''; return; }
    const parts = [set.m, set.g, set.b].filter(Boolean);
    const final = parts.reduce((sum, it) => sum + Math.round((it.price * (100 - this.pct)) / 100), 0);
    const saving = set.total - final;
    const grinderCard = set.g ? this.card('g', set.g, L.grinder) : this.card('g', null, L.grinder, set.m.builtIn ? L.builtin : L.owned);
    this.items.style.setProperty('--n', 3);
    this.items.innerHTML = `${this.card('m', set.m, L.machine, set.m.builtIn ? L.builtin : '')}${grinderCard}${this.card('b', set.b, L.beans, set.b.variant && set.b.variant !== 'Default Title' ? set.b.variant : '')}
      <div class="cs-bundle__total"><small>${L.your_set}</small>${saving > 0 ? `<s>${this.money(set.total)}</s>` : ''}<b>${this.money(final)}</b><p>${saving > 0 ? L.save.replace('__AMOUNT__', this.money(saving)) : '&nbsp;'}</p>
      <button type="button" class="cs-btn cs-btn--white cs-btn--block cs-btn--sm" data-add>${L.add}</button>${this.all.length > 1 ? `<button type="button" class="cs-bbundle__shuffle" data-shuffle>↻ ${L.shuffle}</button>` : ''}${saving > 0 ? `<p class="cs-bbundle__fine">${L.at_checkout}</p>` : ''}</div>`;
    this.items.querySelectorAll('[data-swap]').forEach((b) => b.addEventListener('click', () => this.swap(b.dataset.swap)));
    this.items.querySelector('[data-add]').addEventListener('click', (e) => this.add(e.currentTarget, parts));
    this.items.querySelector('[data-shuffle]')?.addEventListener('click', () => this.shuffle());
  }
  async add(button, parts) {
    const L = this.data.labels; const tag = `${Date.now().toString(36)}:${parts.length}`;
    button.disabled = true; button.textContent = L.adding;
    try {
      await codistyAddToCart(parts.map((p) => ({ id: p.id, quantity: 1, properties: { _cs_bundle: tag } })), this, 'codisty-bundle');
      button.textContent = L.added;
    } catch (err) { alert(err.message); button.textContent = L.add; }
    finally { setTimeout(() => { button.disabled = false; button.textContent = L.add; }, 2200); }
  }
}

const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
define('cs-slider', CsSlider);
define('cs-tabs', CsTabs);
define('cs-finder', CsFinder);
define('cs-quick-add', CsQuickAdd);
define('cs-bundle', CsBundle);
define('cs-budget-bundle', CsBudgetBundle);
define('cs-follow', CsFollow);

/* ------------------------------------------------------------------ */
/* Entrance animations                                                  */
/* ------------------------------------------------------------------ */
function initReveal(root = document) {
  const els = root.querySelectorAll('[data-cs-anim]:not([data-cs-anim="none"]):not(.is-visible)');
  if (designMode || !('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-visible')); return; }
  const io = new IntersectionObserver((entries) => entries.forEach((en) => {
    if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); }
  }), { rootMargin: '0px 0px -8% 0px' });
  els.forEach((el) => io.observe(el));
}
initReveal();
document.addEventListener('shopify:section:load', (e) => initReveal(e.target));

/* ------------------------------------------------------------------ */
/* <cs-search>: predictive search dropdown (products, collections,     */
/* suggested queries) using Shopify's /search/suggest.json             */
/* ------------------------------------------------------------------ */
class CsSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.box = this.querySelector('.cs-search__results');
    this.limit = Number(this.dataset.limit || 5);
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    if (!this.input || !this.box) return;
    this.input.addEventListener('input', () => { clearTimeout(this._t); this._t = setTimeout(() => this.search(), 180); });
    this.input.addEventListener('focus', () => { if (this.box.innerHTML && this.input.value.trim()) this.open(); });
    this.input.addEventListener('keydown', (e) => this.keys(e));
    document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(); });
  }
  money(v) {
    const n = Number(v);
    const fmt = (x, sep = ',', dec = '.') => x.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, sep).replace(/\.(\d+)$/, `${dec}$1`);
    return this.moneyFormat
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, fmt(n, '.', ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(n).toString())
      .replace(/\{\{\s*amount\s*\}\}/, fmt(n));
  }
  async search() {
    const q = this.input.value.trim();
    if (q.length < 2) { this.close(); return; }
    this.abort?.abort(); this.abort = new AbortController();
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const params = new URLSearchParams({ q, 'resources[type]': 'product,collection,query', 'resources[limit]': String(this.limit), 'resources[options][unavailable_products]': this.dataset.unavailable || 'last' });
    try {
      const res = await fetch(`${root}search/suggest.json?${params}`, { signal: this.abort.signal });
      const { resources } = await res.json();
      this.render(q, resources.results || {});
    } catch (e) { /* aborted */ }
  }
  esc(s) { const d = document.createElement('div'); d.textContent = s ?? ''; return d.innerHTML; }
  img(src) { try { const u = new URL(src, window.location.origin); u.searchParams.set('width', '120'); return u.toString(); } catch (e) { return src; } }
  render(q, r) {
    const parts = [];
    if (r.queries?.length) parts.push(`<div class="cs-search__group"><p class="cs-search__group-title">${this.esc(t('suggestions', 'Suggestions'))}</p>${r.queries.map((x) => `<a class="cs-search__item" role="option" href="${x.url}"><span>${x.styled_text || this.esc(x.text)}</span></a>`).join('')}</div>`);
    if (r.products?.length) parts.push(`<div class="cs-search__group"><p class="cs-search__group-title">${this.esc(t('products', 'Products'))}</p>${r.products.map((p) => `<a class="cs-search__item" role="option" href="${p.url}">${p.image ? `<img src="${this.img(p.image)}" alt="" loading="lazy">` : ''}<span><b>${this.esc(p.title)}</b><small>${this.esc(p.vendor || '')}</small></span><span class="cs-search__price">${p.price ? this.money(p.price) : ''}</span></a>`).join('')}</div>`);
    if (r.collections?.length) parts.push(`<div class="cs-search__group"><p class="cs-search__group-title">${this.esc(t('collections', 'Collections'))}</p>${r.collections.map((c) => `<a class="cs-search__item" role="option" href="${c.url}"><span><b>${this.esc(c.title)}</b></span></a>`).join('')}</div>`);
    const url = `${this.dataset.searchUrl || '/search'}?q=${encodeURIComponent(q)}&options[prefix]=last`;
    parts.push(`<a class="cs-search__all" href="${url}">${this.esc(t('view_all', 'View all results for'))} “${this.esc(q)}”</a>`);
    this.box.innerHTML = parts.join('');
    this.open();
  }
  // While results are open, lift this section above the following sections (they create their own layers)
  open() { this.box.hidden = false; this.input.setAttribute('aria-expanded', 'true'); this.closest('.shopify-section')?.classList.add('cs-search-active'); }
  close() { this.box.hidden = true; this.input.setAttribute('aria-expanded', 'false'); this.sel = -1; this.closest('.shopify-section')?.classList.remove('cs-search-active'); }
  keys(e) {
    const items = [...this.box.querySelectorAll('[role="option"], .cs-search__all')];
    if (e.key === 'Escape') return this.close();
    if (!items.length || this.box.hidden) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      this.sel = ((this.sel ?? -1) + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
      items.forEach((el, i) => el.setAttribute('aria-selected', String(i === this.sel)));
      items[this.sel].scrollIntoView({ block: 'nearest' });
    }
    if (e.key === 'Enter' && this.sel >= 0 && items[this.sel]) { e.preventDefault(); window.location.href = items[this.sel].href; }
  }
}
define('cs-search', CsSearch);

/* ------------------------------------------------------------------ */
/* <cs-video>: click-to-play YouTube / Vimeo (no third-party JS first) */
/* ------------------------------------------------------------------ */
class CsVideo extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('data-autoplay') && !designMode) { this.load(); return; }
    this.querySelector('.cs-video-embed__play')?.addEventListener('click', () => this.load());
  }
  load() {
    if (this.querySelector('iframe')) return;
    const f = document.createElement('iframe');
    f.src = this.dataset.src;
    f.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    f.allowFullscreen = true;
    f.title = 'Video';
    this.replaceChildren(f);
  }
}

/* ------------------------------------------------------------------ */
/* <cs-countdown>                                                       */
/* ------------------------------------------------------------------ */
class CsCountdown extends HTMLElement {
  connectedCallback() {
    this.end = Number(this.dataset.end) * 1000;
    if (!this.end) return;
    this.units = Object.fromEntries([...this.querySelectorAll('[data-u]')].map((el) => [el.dataset.u, el]));
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }
  disconnectedCallback() { clearInterval(this.timer); }
  tick() {
    const left = Math.max(0, this.end - Date.now());
    if (!left) {
      clearInterval(this.timer);
      if (this.dataset.hide === 'true' && !designMode) { this.closest('.shopify-section')?.setAttribute('hidden', ''); return; }
      const p = document.createElement('p'); p.className = 'cs-countdown__expired'; p.textContent = this.dataset.expired || '';
      this.replaceChildren(p);
      return;
    }
    const s = Math.floor(left / 1000);
    const vals = { d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
    for (const [k, el] of Object.entries(this.units)) el.textContent = String(vals[k]).padStart(2, '0');
  }
}

/* ------------------------------------------------------------------ */
/* <cs-recommendations>: loads product recommendations section          */
/* ------------------------------------------------------------------ */
class CsRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.loaded || !this.dataset.url) return;
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      this.load();
    }, { rootMargin: '0px 0px 400px 0px' });
    io.observe(this);
  }
  async load() {
    this.loaded = true;
    try {
      const html = await (await fetch(this.dataset.url)).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('cs-recommendations');
      if (fresh && fresh.innerHTML.trim()) { this.innerHTML = fresh.innerHTML; initReveal(this); }
      else if (!designMode) this.closest('.shopify-section')?.setAttribute('hidden', '');
    } catch (e) { /* keep server-rendered content */ }
  }
}

/* ------------------------------------------------------------------ */
/* <cs-gift>: "buy X get 1 free" chooser                                */
/* ------------------------------------------------------------------ */
class CsGift extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.pill = this.querySelector('[data-cs-gift-open]');
    this.state = this.readState(this);
    this.querySelectorAll('[data-cs-gift-close]').forEach((b) => b.addEventListener('click', () => this.dismiss()));
    this.dialog?.addEventListener('cancel', (e) => { e.preventDefault(); this.dismiss(); });
    this.dialog?.addEventListener('click', (e) => { if (e.target === this.dialog) this.dismiss(); });
    this.pill?.addEventListener('click', () => this.open());
    this.querySelectorAll('[data-cs-gift-choice]').forEach((f) => f.addEventListener('submit', (e) => this.choose(e)));
    this.onCart = (e) => {
      if (e.detail?.source === 'codisty-gift') return;
      const p = e.promise || Promise.resolve();
      p.then(() => this.refresh(true)).catch(() => {});
    };
    import('@shopify/events').then(({ StandardEvents }) => document.addEventListener(StandardEvents.cartLinesUpdate, this.onCart)).catch(() => {});
    document.addEventListener('codisty:cart-updated', () => this.refresh(true));
    this.render(false);
  }
  readState(root) {
    try { return JSON.parse(root.querySelector('[data-cs-gift-state]').textContent); } catch (e) { return { earned: 0, claimed: 0, gifts: [] }; }
  }
  get pending() { return Math.max(0, (this.state.earned || 0) - (this.state.claimed || 0)); }
  async refresh(prompt) {
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const html = await (await fetch(`${root}?section_id=cs-gift-state`)).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      this.state = this.readState(doc);
      await this.trimExcess();
      this.render(prompt);
    } catch (e) { /* ignore */ }
  }
  /* Removes free items the cart no longer qualifies for (e.g. the shopper removed a paid bag). */
  async trimExcess() {
    let extra = (this.state.claimed || 0) - (this.state.earned || 0);
    if (extra <= 0) return;
    const gifts = (this.state.gifts || []).filter(Boolean).map((g) => { const i = g.lastIndexOf(':'); return { key: g.slice(0, i), qty: Number(g.slice(i + 1)) }; });
    for (const g of gifts.reverse()) {
      if (extra <= 0) break;
      const take = Math.min(extra, g.qty);
      await codistyChangeLine(g.key, g.qty - take, this, 'codisty-gift');
      extra -= take;
      this.state.claimed -= take;
    }
  }
  dismissedAt() { try { return Number(sessionStorage.getItem(this.dataset.dismissKey) || 0); } catch (e) { return 0; } }
  render(prompt) {
    const pending = this.pending;
    if (this.pill) {
      this.pill.hidden = pending === 0;
      const c = this.pill.querySelector('[data-cs-gift-count]'); if (c) c.textContent = pending > 1 ? String(pending) : '';
    }
    if (pending === 0) { this.close(); return; }
    if (prompt && this.state.earned > this.dismissedAt()) this.open();
  }
  open() { if (this.dialog && !this.dialog.open) this.dialog.showModal(); }
  close() { if (this.dialog?.open) this.dialog.close(); }
  dismiss() { try { sessionStorage.setItem(this.dataset.dismissKey, String(this.state.earned || 0)); } catch (e) { /* ignore */ } this.close(); }
  async choose(e) {
    e.preventDefault();
    const form = e.currentTarget;
    const id = new FormData(form).get('id');
    const btn = form.querySelector('button[type="submit"]');
    if (!id || !btn) return;
    const label = btn.textContent;
    btn.disabled = true; btn.textContent = t('adding', 'Adding…');
    try {
      await codistyAddToCart([{ id, quantity: 1, properties: { _cs_gift: '1' } }], this, 'codisty-gift');
      this.state.claimed = (this.state.claimed || 0) + 1;
      this.render(false);
      if (this.pending > 0) this.open();
    } catch (err) {
      alert(err.message);
    } finally {
      btn.disabled = false; btn.textContent = label;
    }
  }
}

/* Change a cart line quantity and let Horizon refresh the drawer / icon. */
export async function codistyChangeLine(key, quantity, sourceEl, source = 'codisty') {
  let CartLinesUpdateEvent = null;
  try { ({ CartLinesUpdateEvent } = await import('@shopify/events')); } catch (e) { /* not a Horizon theme */ }
  const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
  const deferred = CartLinesUpdateEvent ? CartLinesUpdateEvent.createPromise() : null;
  if (CartLinesUpdateEvent) {
    (sourceEl || document).dispatchEvent(new CartLinesUpdateEvent({ action: quantity === 0 ? 'remove' : 'update', context: 'cart', lines: [{ id: key, quantity }], promise: deferred.promise }));
  }
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  try {
    const res = await fetch(`${root}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity, sections: sectionIds.join(',') }) });
    const json = await res.json();
    if (deferred) deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(json), detail: { items: json.items, source, sourceId: source, itemCount: json.item_count, sections: json.sections, didError: Boolean(json.status) } });
    return json;
  } catch (err) {
    if (deferred) deferred.reject(err);
    throw err;
  }
}

/* Footer menus: always open on desktop, collapsible accordions on mobile. */
function initFooterMenus(root = document) {
  const mq = window.matchMedia('(min-width: 750px)');
  const menus = root.querySelectorAll('.cs-footer__menu');
  const sync = () => menus.forEach((d) => { d.open = mq.matches; });
  sync();
  mq.addEventListener?.('change', sync);
  menus.forEach((d) => d.querySelector('summary')?.addEventListener('click', (e) => { if (mq.matches) e.preventDefault(); }));
}

define('cs-video', CsVideo);
define('cs-countdown', CsCountdown);
define('cs-recommendations', CsRecommendations);
define('cs-gift', CsGift);
initFooterMenus();
document.addEventListener('shopify:section:load', (e) => initFooterMenus(e.target));

/* ------------------------------------------------------------------ */
/* <cs-facets>: filters, sorting, load more / infinite scroll (AJAX)    */
/* ------------------------------------------------------------------ */
class CsFacets extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.addEventListener('change', (e) => {
      const el = e.target;
      if (el.matches('[data-cs-sort]')) {
        const params = new URLSearchParams(window.location.search);
        params.set('sort_by', el.value); params.delete('page');
        this.go(`${window.location.pathname}?${params}`);
        return;
      }
      const form = el.closest('[data-cs-facets-form]');
      if (form && el.type !== 'number') this.submit(form);
    });
    this.addEventListener('input', (e) => {
      if (e.target.type !== 'number') return;
      const form = e.target.closest('[data-cs-facets-form]');
      clearTimeout(this._debounce);
      this._debounce = setTimeout(() => form && this.submit(form), 700);
    });
    this.addEventListener('submit', (e) => { if (e.target.matches('[data-cs-facets-form]')) { e.preventDefault(); this.submit(e.target); } });
    this.addEventListener('click', (e) => {
      const link = e.target.closest('[data-cs-facet-link]');
      if (link) { e.preventDefault(); this.go(link.href); return; }
      const more = e.target.closest('[data-cs-next-page]');
      if (more) { e.preventDefault(); this.loadMore(more); return; }
      if (e.target.closest('[data-cs-facets-open]')) { this.drawer()?.showModal(); return; }
      if (e.target.closest('[data-cs-facets-close]')) { this.drawer()?.close(); return; }
      const toggle = e.target.closest('[data-cs-facet-more]');
      if (toggle) {
        const wrap = toggle.parentElement; const on = wrap.classList.toggle('is-expanded');
        if (!toggle.dataset.more) toggle.dataset.more = toggle.textContent;
        toggle.textContent = on ? toggle.dataset.less : toggle.dataset.more;
      }
    });
    this.addEventListener('click', (e) => { const d = this.drawer(); if (d && e.target === d) d.close(); });
    window.addEventListener('popstate', () => this.go(window.location.href, false));
    this.observeInfinite();
  }
  drawer() { return this.querySelector('.cs-plp__drawer'); }
  submit(form) {
    const params = new URLSearchParams();
    for (const [k, v] of new FormData(form)) if (String(v).trim() !== '') params.append(k, v);
    this.go(`${window.location.pathname}?${params}`);
  }
  async fetchSection(url) {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', this.sectionId);
    const res = await fetch(u.toString());
    return new DOMParser().parseFromString(await res.text(), 'text/html');
  }
  async go(url, push = true) {
    this.classList.add('is-loading');
    try {
      const doc = await this.fetchSection(url);
      const fresh = doc.querySelector('cs-facets');
      if (!fresh) { window.location.href = url; return; }
      const drawerOpen = this.drawer()?.open;
      const openState = [...this.querySelectorAll('details.cs-facet')].map((d) => d.open);
      const focusName = document.activeElement && this.contains(document.activeElement) ? `${document.activeElement.name}|${document.activeElement.value}` : null;
      // Replace the results area and the filter forms, keep the dialog element (so it stays open).
      const swap = (sel) => { const a = this.querySelector(sel); const b = fresh.querySelector(sel); if (a && b) a.replaceWith(b); else if (a && !b) a.remove(); };
      swap('.cs-plp__main');
      const forms = [...this.querySelectorAll('[data-cs-facets-form]')];
      forms.forEach((f) => { const nf = fresh.querySelector(`#${f.id}`); if (nf) f.innerHTML = nf.innerHTML; });
      const foot = fresh.querySelector('.cs-plp__drawer-foot'); const curFoot = this.querySelector('.cs-plp__drawer-foot'); if (foot && curFoot) curFoot.innerHTML = foot.innerHTML;
      this.querySelectorAll('details.cs-facet').forEach((d, i) => { if (openState[i] !== undefined) d.open = openState[i]; });
      if (drawerOpen && !this.drawer()?.open) this.drawer()?.showModal();
      if (focusName) { const [n, v] = focusName.split('|'); [...this.querySelectorAll('input')].find((i) => i.name === n && (i.type === 'number' || i.value === v))?.focus({ preventScroll: true }); }
      if (push) history.pushState({}, '', url);
      initReveal(this);
      this.observeInfinite();
      if (!drawerOpen) {
        const top = this.getBoundingClientRect().top + window.scrollY - 120;
        if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
      }
    } catch (e) {
      window.location.href = url;
    } finally {
      this.classList.remove('is-loading');
    }
  }
  async loadMore(link) {
    const box = link.closest('.cs-plp__more');
    if (!box || box.classList.contains('is-loading')) return;
    box.classList.add('is-loading');
    try {
      const doc = await this.fetchSection(link.href);
      const grid = this.querySelector('[data-cs-grid]');
      const newGrid = doc.querySelector('[data-cs-grid]');
      if (grid && newGrid) grid.append(...newGrid.children);
      const newMore = doc.querySelector('.cs-plp__more');
      if (newMore) box.replaceWith(newMore); else box.remove();
      initReveal(this);
      this.observeInfinite();
    } catch (e) {
      window.location.href = link.href;
    }
  }
  observeInfinite() {
    this._io?.disconnect();
    const box = this.querySelector('[data-cs-more="infinite"]');
    if (!box || !('IntersectionObserver' in window)) return;
    this._io = new IntersectionObserver((entries) => {
      if (entries.some((en) => en.isIntersecting)) { const a = box.querySelector('[data-cs-next-page]'); if (a) this.loadMore(a); }
    }, { rootMargin: '0px 0px 600px 0px' });
    this._io.observe(box);
  }
}
define('cs-facets', CsFacets);

/* ------------------------------------------------------------------ */
/* <cs-brand-directory>: search, A–Z letters, type tabs, logos / list   */
/* ------------------------------------------------------------------ */
class CsBrandDirectory extends HTMLElement {
  connectedCallback() {
    this.results = this.querySelector('[data-bd-results]');
    this.items = [...this.querySelectorAll('.cs-bd__item')];
    this.state = { q: '', letter: '', group: 'all' };
    // letter headings, inserted before the first brand of each letter
    let last = null;
    this.items.forEach((it) => {
      if (it.dataset.letter !== last) {
        last = it.dataset.letter;
        const h = document.createElement('p'); h.className = 'cs-bd__head'; h.dataset.head = last; h.textContent = last === '#' ? '123' : last;
        it.before(h);
      }
    });
    this.heads = [...this.querySelectorAll('.cs-bd__head')];
    this.querySelector('[data-bd-search]')?.addEventListener('input', (e) => { this.state.q = e.target.value.trim().toLowerCase(); this.apply(); });
    this.addEventListener('click', (e) => {
      const l = e.target.closest('[data-bd-letter]'); const g = e.target.closest('[data-bd-group]'); const v = e.target.closest('[data-bd-view]');
      if (l) { this.state.letter = l.dataset.bdLetter; this.press('[data-bd-letter]', l); this.apply(); if (l.dataset.bdLetter) this.results.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
      if (g) { this.state.group = g.dataset.bdGroup; this.press('[data-bd-group]', g); this.apply(); }
      if (v) { this.dataset.view = v.dataset.bdView; this.press('[data-bd-view]', v); try { localStorage.setItem('cs-bd-view', v.dataset.bdView); } catch (err) { /* ignore */ } }
    });
    try { const saved = localStorage.getItem('cs-bd-view'); if (saved) { this.dataset.view = saved; this.press('[data-bd-view]', this.querySelector(`[data-bd-view="${saved}"]`)); } } catch (err) { /* ignore */ }
  }
  press(sel, el) { this.querySelectorAll(sel).forEach((b) => b.setAttribute('aria-pressed', String(b === el))); }
  apply() {
    const { q, letter, group } = this.state;
    const visible = new Set();
    this.items.forEach((it) => {
      const ok = (!q || it.dataset.name.includes(q)) && (!letter || it.dataset.letter === letter) && (group === 'all' || it.dataset.group === group);
      it.hidden = !ok; if (ok) visible.add(it.dataset.letter);
    });
    this.heads.forEach((h) => { h.hidden = !visible.has(h.dataset.head); });
    this.querySelectorAll('[data-bd-letter]').forEach((b) => { if (b.dataset.bdLetter) b.classList.toggle('is-dim', !this.items.some((it) => it.dataset.letter === b.dataset.bdLetter && (group === 'all' || it.dataset.group === group))); });
    this.querySelector('[data-bd-empty]').hidden = visible.size > 0;
  }
}
define('cs-brand-directory', CsBrandDirectory);

/* ------------------------------------------------------------------ */
/* <cs-delivery-area>: ask for the delivery area before checkout        */
/* ------------------------------------------------------------------ */
const norm = (s) => String(s || '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/['’`]/g, '').replace(/[^a-z0-9؀-ۿ]+/g, ' ').trim();
class CsDeliveryArea extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.input = this.querySelector('[data-cs-da-input]');
    this.list = this.querySelector('[data-cs-da-results]');
    this.go = this.querySelector('[data-cs-da-go]');
    this.chosenBox = this.querySelector('[data-cs-da-chosen]');
    this.main = this.dataset.main || 'Beirut';
    this.choice = (() => { try { return JSON.parse(localStorage.getItem('cs-delivery-area')); } catch (e) { return null; } })();
    this.input.addEventListener('input', () => this.search());
    this.input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); this.list.querySelector('li')?.click(); } });
    this.list.addEventListener('click', (e) => { const li = e.target.closest('li[data-area]'); if (li) this.pick([li.dataset.area, li.dataset.district]); });
    this.querySelector('[data-cs-da-change]')?.addEventListener('click', () => { this.choice = null; this.render(); this.input.focus(); });
    this.querySelectorAll('[data-cs-da-close]').forEach((b) => b.addEventListener('click', () => this.dialog.close()));
    this.querySelector('[data-cs-da-skip]')?.addEventListener('click', () => this.checkout(null));
    this.querySelector('[data-cs-da-form]').addEventListener('submit', (e) => { e.preventDefault(); if (this.choice) this.checkout(this.choice); });
    this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
    // Intercept every way of reaching checkout (cart drawer, cart page, dynamic checkout links)
    document.addEventListener('click', (e) => {
      const el = e.target.closest('button[name="checkout"], input[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]');
      if (!el || designMode || this.bypass) return;
      e.preventDefault(); e.stopImmediatePropagation(); this.open();
    }, true);
    document.addEventListener('submit', (e) => {
      const f = e.target;
      if (designMode || this.bypass || !(f.action || '').includes('/checkout') && !(e.submitter && e.submitter.name === 'checkout')) return;
      e.preventDefault(); e.stopImmediatePropagation(); this.open();
    }, true);
  }
  async load() {
    if (this.areas) return this.areas;
    const data = await (await fetch(this.dataset.src)).json();
    this.areas = data.map(([area, district]) => ({ area, district, key: norm(`${area} ${district}`), a: norm(area) }));
    return this.areas;
  }
  async open() {
    this.render();
    if (!this.dialog.open) this.dialog.showModal();
    await this.load();
    if (!this.choice) { this.input.focus(); this.search(); }
  }
  label(c) { return c[1] && norm(c[0]) !== norm(c[1]) ? `${c[0]}, ${c[1]}` : c[0]; }
  fee(c) {
    const tpl = norm(c[1]) === norm(this.main) ? t('delivery_main', 'Within __D__') : t('delivery_other', 'Outside __D__');
    return tpl.replace('__D__', this.main);
  }
  render() {
    const has = Boolean(this.choice);
    this.chosenBox.hidden = !has;
    this.input.closest('label').hidden = has;
    this.list.hidden = has;
    this.go.disabled = !has;
    if (has) { this.querySelector('[data-cs-da-name]').textContent = this.label(this.choice); this.querySelector('[data-cs-da-fee]').textContent = this.fee(this.choice); }
  }
  search() {
    if (!this.areas) return;
    const q = norm(this.input.value);
    let hits;
    if (!q) hits = this.areas.filter((x) => norm(x.district) === norm(this.main)).slice(0, 8);
    else {
      const words = q.split(' ');
      const main = norm(this.main);
      const score = (x) => {
        const aw = x.a.split(' ');
        let s = x.a === q ? 8 : x.a.startsWith(q) ? 5 : aw.some((w) => w.startsWith(words[0])) ? 4 : 0;
        if (norm(x.district) === main) s += 5; // Beirut areas first (most orders)
        if (norm(x.district) === x.a) s += 1;  // district / city names
        return s;
      };
      // tolerant match: "achrafieh" finds "Achrafiye", "jounie" finds "Jounieh"
      const fits = (k, w) => k.startsWith(w) || (w.length >= 5 && k.startsWith(w.slice(0, w.length - 2)));
      hits = this.areas.filter((x) => words.every((w) => x.key.split(' ').some((k) => fits(k, w)) || x.key.includes(w)))
        .map((x) => [score(x), x]).sort((a, b) => b[0] - a[0] || a[1].area.length - b[1].area.length).map(([, x]) => x)
        .slice(0, 8);
    }
    this.list.innerHTML = hits.length
      ? hits.map((x) => `<li role="option" tabindex="0" data-area="${x.area.replace(/"/g, '&quot;')}" data-district="${x.district}"><b>${x.area}</b><span>${x.district}</span></li>`).join('')
      : `<li class="cs-da__none">${t('delivery_none', 'No area found')}</li>`;
  }
  pick(c) {
    this.choice = c;
    try { localStorage.setItem('cs-delivery-area', JSON.stringify(c)); } catch (e) { /* ignore */ }
    this.render();
    this.go.focus();
  }
  async checkout(c) {
    this.go.disabled = true;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const params = new URLSearchParams();
    if (c) {
      const city = this.label(c);
      try { await fetch(`${root}cart/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { 'Delivery area': city } }) }); } catch (e) { /* ignore */ }
      params.set('checkout[shipping_address][city]', city);
      params.set('checkout[shipping_address][country]', this.dataset.country || 'LB');
    }
    this.bypass = true;
    window.location.href = `${root}checkout${params.toString() ? `?${params}` : ''}`;
  }
}
define('cs-delivery-area', CsDeliveryArea);

/* Expose the cart helper for app embeds (CodiGuide chat). */
window.codistyAddToCart = codistyAddToCart;

/* Marketing events (Shopify customer events). A custom pixel forwards them to Meta:
   codisty:whatsapp_click → Contact · codisty:newsletter_signup → Lead · codisty:chat_open → ChatOpened */
(function () {
  const publish = (name, data) => { try { window.Shopify && Shopify.analytics && Shopify.analytics.publish(name, data || {}); } catch (e) {} };
  window.codistyTrack = publish;
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href*="wa.me/"], a[href*="api.whatsapp.com"], a[href*="whatsapp://"]');
    if (!a) return;
    publish('codisty:whatsapp_click', {
      placement: a.classList.contains('cs-wa-float') ? 'floating' : a.closest('.cs-wa-order, [class*="whatsapp"]') ? 'product' : 'link',
      page: location.pathname,
      product_title: document.querySelector('h1')?.textContent?.trim()?.slice(0, 120) || ''
    });
  }, true);
  document.addEventListener('submit', (e) => {
    const f = e.target;
    if (f.matches && f.matches('form.cs-news, form[id^="cs-news-"]')) publish('codisty:newsletter_signup', { page: location.pathname });
  }, true);
})();
